import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addContent, getHighestOrder, storage } from "../services/firebase";
import { NewContentItem, ContentItem, EquipmentProduct } from "../types";
import { useNavigate } from "react-router-dom";
import { generateSlug, getContentPath } from "./utils/generateSlug";
import { MarkdownField } from "./ui/MarkDown";
import Cropper from "react-easy-crop";
import { validateImageFile } from "../components/utils/imageValidation";
import { cropImageFile } from "../components/utils/cropImage";
import { resizeImage } from "./utils/resizeImage";
import CharCounter from "./utils/CharCounter";
import VideoUploader from "./ui/VideoUploader";
import {
  ArrowLeft,
  ChevronDown,
  Cross,
  CrossIcon,
  PlusIcon,
  Save,
  Trash,
  X,
} from "lucide-react";
import { Modal, Table } from "antd";
import ExplorePage from "../pages/Explore";
import DraftIdeas from "../pages/DraftIdeas";
import { set } from "lodash";

const AdminForm: React.FC = () => {
  const navigate = useNavigate();
  const { theme, changeTheme } = useTheme();
  const [contentType, setContentType] = useState<
    "punishment" | "challenge" | "draft" | "draftIdeas"
  >("punishment");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [imageDescription, setImageDescription] = useState("");
  const [flRating, setFlRating] = useState("");
  const [embarrassmentLevel, setEmbarrassmentLevel] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [longTermEffects, setLongTermEffects] = useState("");
  const [equipmentLocker, setEquipmentLocker] = useState("");
  const [equipmentLockerSubHeading, setEquipmentLockerSubHeading] =
    useState("");
  const [equipmentNeeded, setEquipmentNeeded] = useState<string[]>([""]);
  const [timeToComplete, setTimeToComplete] = useState("");
  const [timeToCompleteSubHeading, setTimeToCompleteSubHeading] = useState("");
  const [costToComplete, setCostToComplete] = useState("");
  const [costToCompleteSubHeading, setCostToCompleteSubHeading] = useState("");
  const [waysToSpiceUp, setWaysToSpiceUp] = useState<string[]>([""]);
  const [instructions, setInstructions] = useState<
    { description: string; image?: File | string | null }[]
  >([{ description: "" }]);
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [image, setImage] = useState<any>(null);
  const [cardImage, setCardImage] = useState<File | null>(null);
  const [imageCredit, setImageCredit] = useState("");
  const [imageCreditLink, setImageCreditLink] = useState("");
  const [mobileHeaderImage, setMobileHeaderImage] = useState<File | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 9, y: 16 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageType, setImageType] = useState<string | null>(null);
  const [nextOrder, setNextOrder] = useState<number | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [viewOption, setViewOption] = useState<
    "punishments" | "draftIdeas" | "form"
  >("punishments");

  // Keep the original fields for backward compatibility
  const [equipmentImage, setEquipmentImage] = useState<File | null>(null);
  const [equipmentImageLink, setEquipmentImageLink] = useState("");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Add state for multiple equipment products
  const [equipmentProducts, setEquipmentProducts] = useState<
    EquipmentProduct[]
  >([]);

  //Refs to clear the cropper selected images
  const imageRef = useRef<HTMLInputElement | null>(null);
  const cardImageRef = useRef<HTMLInputElement | null>(null);
  const mobileHeaderImageRef = useRef<HTMLInputElement | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // change theme to light always
    changeTheme("light");
  }, [theme]);

  const uploadImage = async (
    file: File | null,
    path: string
  ): Promise<string | null> => {
    if (!file) return null;
    try {
      const storageRef = ref(storage, `${path}/${file.name}-${Date.now()}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const handleEquipmentChange = (index: number, value: string) => {
    const updatedEquipment = [...equipmentNeeded];
    updatedEquipment[index] = value;
    setEquipmentNeeded(updatedEquipment);
  };

  const handleEquipmentProductChange = (
    index: number,
    field: keyof EquipmentProduct,
    value: any
  ) => {
    const updatedProducts = [...equipmentProducts];

    // Create the product if it doesn't exist
    if (!updatedProducts[index]) {
      updatedProducts[index] = { name: "" };
    }

    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value,
    };

    setEquipmentProducts(updatedProducts);
  };

  const handleWaysToSpiceUpChange = (index: number, value: string) => {
    const updatedWays = [...waysToSpiceUp];
    updatedWays[index] = value;
    setWaysToSpiceUp(updatedWays);
  };

  const handleInstructionChange = (index: number, value: string) => {
    const updatedInstructions = [...instructions];
    updatedInstructions[index].description = value;
    setInstructions(updatedInstructions);
  };

  const handleInstructionImageChange = async (
    index: number,
    file: File | null
  ) => {
    if (file) {
      const storageRef = ref(
        storage,
        `instructions/${file.name}-${Date.now()}`
      );
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const updatedInstructions = [...instructions];
      updatedInstructions[index].image = downloadURL;
      setInstructions(updatedInstructions);
    } else {
      const updatedInstructions = [...instructions];
      updatedInstructions[index].image = undefined;
      setInstructions(updatedInstructions);
    }
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files && e.target.files[0]) {
  //     setImage(e.target.files[0]);
  //   }
  // };

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        setError(validationError);
        return;
      }

      setLoadingCropper(true);
      try {
        const { resizedUrl, resizedFile } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
        setError(null);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(
    async (imageType: string | null) => {
      if (!tempImageUrl || !croppedArea) return;

      const cropped = await cropImageFile(tempImageUrl, croppedArea);
      if (cropped) {
        if (imageType === "main-image") {
          setImage(cropped);
        } else if (imageType === "card-image") {
          setCardImage(cropped);
        } else if (imageType === "mobile-header-image") {
          setMobileHeaderImage(cropped);
        }
        setSelectedImage(cropped);
        setImagePreview(URL.createObjectURL(cropped));
      }

      setShowCropper(false);
    },
    [tempImageUrl, croppedArea]
  );

  const handleProductImageChange = async (index: number, file: File | null) => {
    if (!file) return;
    try {
      const downloadURL = await uploadImage(file, "product-images");
      handleEquipmentProductChange(index, "image", downloadURL);
    } catch (error) {
      console.error("Error uploading product image:", error);
    }
  };

  const handleUploadSuccess = (url: string) => {
    console.log("Video uploaded successfully:", url);
    setVideoUrl(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const mainImageUrl = await uploadImage(image, "main-images");
      const cardImageUrl = await uploadImage(cardImage, "card-images");
      const mobileHeaderImageUrl = await uploadImage(
        mobileHeaderImage,
        "mobile-headers"
      );
      const equipmentImageUrl = await uploadImage(
        equipmentImage,
        "equipment-images"
      );

      // Process equipment product images
      const processedProducts = await Promise.all(
        equipmentProducts
          .filter((product) => product.name.trim() !== "") // Filter out empty products
          .map(async (product) => {
            if (product.image instanceof File) {
              const imageUrl = await uploadImage(
                product.image as File,
                "product-images"
              );
              return {
                ...product,
                image: imageUrl,
              };
            }
            return product;
          })
      );

      const slug = generateSlug(title);
      const newContentItem: Omit<ContentItem, "id"> = {
        type: contentType,
        title: title || "",
        slug,
        subtitle: subtitle || "",
        image: mainImageUrl || "",
        imageDescription: imageDescription || "",
        flRating: flRating || "",
        embarrassmentLevel: embarrassmentLevel || "",
        riskLevel: riskLevel || "",
        longTermEffects: longTermEffects || "",
        equipmentLocker: equipmentLocker || "",
        equipmentLockerSubHeading: equipmentLockerSubHeading || "",
        equipmentNeeded: equipmentNeeded.filter((item) => item.trim() !== ""),
        timeToComplete: timeToComplete || "",
        timeToCompleteSubHeading: timeToCompleteSubHeading || "",
        costToComplete: costToComplete || "",
        costToCompleteSubHeading: costToCompleteSubHeading || "",
        waysToSpiceUp: waysToSpiceUp.filter((item) => item.trim() !== ""),
        instructions: instructions.map((instruction) => ({
          description: instruction.description || "",
          image: instruction.image || "",
        })),
        tags: tags || [],
        createdAt: new Date(),
        cardImage: cardImageUrl || "",
        imageCredit: imageCredit || "Fantasy Loser",
        mobileHeaderImage: mobileHeaderImageUrl || "",
        equipmentImage: equipmentImageUrl || "",
        equipmentImageLink: equipmentImageLink || "",
        equipmentProducts:
          processedProducts.length > 0 ? processedProducts : [],
        order: nextOrder ?? 0,
        videoUrl: videoUrl || "",
        imageCreditLink: imageCreditLink || "",
      };

      const newContentId = await addContent(newContentItem);
      alert("Content added successfully!");

      // navigate(getContentPath(newContentId, slug, contentType));
      setViewOption("punishments");
      // navigate("/admin");

      // Reset form
      setContentType("punishment");
      setTitle("");
      setSubtitle("");
      setImage(null);
      setImageDescription("");
      setFlRating("");
      setEmbarrassmentLevel("");
      setRiskLevel("");
      setLongTermEffects("");
      setEquipmentLocker("");
      setEquipmentLockerSubHeading("");
      setEquipmentNeeded([""]);
      setTimeToComplete("");
      setTimeToCompleteSubHeading("");
      setCostToComplete("");
      setCostToCompleteSubHeading("");
      setWaysToSpiceUp([""]);
      setInstructions([{ description: "" }]);
      setTags([]);
      setEquipmentProducts([]);
      setVideoUrl("");
    } catch (error) {
      if (error instanceof Error) {
        setError(`Error adding content: ${error.message}`);
      } else {
        setError("An unknown error occurred while adding content");
      }
    }
    finally { 
      setIsSaving(false);
    }
  };

  useEffect(() => {
    getHighestOrderByContentType();
  }, [contentType]);

  const getHighestOrderByContentType = async () => {
    const highestOrder = await getHighestOrder(contentType);
    setNextOrder(highestOrder + 1);
  };

  const dummyTableData = [
    {
      key: "1",
      id: "1",
      title: "Sample Punishment 1",
      type: "punishment",
      createdAt: "2025-12-15",
      status: "Published",
    },
    {
      key: "2",
      id: "2",
      title: "Sample Punishment 2",
      type: "punishment",
      createdAt: "2025-12-14",
      status: "Draft",
    },
    {
      key: "3",
      id: "3",
      title: "Sample Punishment 3",
      type: "punishment",
      createdAt: "2025-12-13",
      status: "Published",
    },
  ];

  const tableColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: () => (
        <button className="text-blue-500 hover:text-blue-700 underline">
          Edit
        </button>
      ),
    },
  ];

  const handleEditClick = (buttonText: string) => {
    setModalTitle(buttonText);
    if (buttonText === "Edit Punishments") {
      navigate("/edit-punishments");
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setModalTitle("");
  };

  const handleViewOptionChange = (value: "punishments" | "draftIdeas") => {
    setViewOption(value);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Content Management</h2>

          {/* Dropdown Menu */}
          <div className="relative">
            <div className="flex items-center gap-4">
              <button
                onClick={() =>
                  setViewOption(viewOption === "form" ? "punishments" : "form")
                }
                className="flex items-center gap-2 px-4 py-2 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] transition-all duration-200"
              >
                {viewOption === "form" ? (
                  <ArrowLeft size={18} />
                ) : (
                  <PlusIcon size={18} />
                )}
                {viewOption === "form" ? "Back" : "Add Content"}
              </button>
              {viewOption !== "form" && (
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] transition-all duration-200"
                >
                  {viewOption === "punishments" ? "Punishments" : "Draft Ideas"}
                  <ChevronDown size={18} />
                </button>
              )}
            </div>

            {/* Overlay to block clicks when dropdown is active */}
            {showDropdown && (
              <>
                <div
                  className="fixed inset-0 bg-gray-500 bg-opacity-30 z-40"
                  onClick={() => setShowDropdown(false)}
                  aria-label="Close dropdown overlay"
                />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      handleViewOptionChange("punishments");
                      setShowDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-t-lg transition-colors"
                  >
                    Punishments
                  </button>
                  <button
                    onClick={() => {
                      handleViewOptionChange("draftIdeas");
                      setShowDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-b-lg transition-colors border-t border-gray-200"
                  >
                    Draft Ideas
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal with Table */}
        <Modal
          title={modalTitle}
          open={isModalVisible}
          onCancel={handleModalClose}
          width={900}
          footer={null}
        >
          <Table
            columns={tableColumns}
            dataSource={dummyTableData}
            pagination={{ pageSize: 10 }}
          />
        </Modal>
        {viewOption === "punishments" && <ExplorePage />}
        {viewOption === "draftIdeas" && <DraftIdeas />}
        {viewOption === "form" && (
          <form
            onSubmit={handleSubmit}
            className={`${
              theme === "dark"
                ? "bg-gray-800 text-white"
                : "bg-white text-gray-800"
            } `}
          >
            <div className="fldWrap">
              <label htmlFor="contentType">
                <span>Content Type</span>
              </label>
              <select
                id="contentType"
                value={contentType}
                onChange={(e) =>
                  setContentType(
                    e.target.value as
                      | "punishment"
                      | "challenge"
                      | "draft"
                      | "draftIdeas"
                  )
                }
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              >
                <option value="punishment">Punishment</option>
                <option value="draftIdeas">Draft Ideas</option>
                {/* <option value="challenge">Challenge</option>
            <option value="draft">Draft Content</option> */}
              </select>
            </div>

            <div className="fldWrap">
              <label htmlFor="title">
                <span>Title</span>
                <CharCounter value={title} max={50} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={50}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                    theme === "dark"
                      ? "bg-gray-700 text-white"
                      : "text-gray-700"
                  } leading-tight focus:outline-none focus:shadow-outline`}
                  required
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="subtitle">
                <span>Subtitle</span>
              </label>
              <MarkdownField
                value={subtitle}
                onChange={(value) => setSubtitle(value || "")}
                placeholder="Enter subtitle with optional formatting"
                minHeight={100}
              />
            </div>

            <div className="fldWrap">
              <label htmlFor="image">
                <span>Main Image</span>
              </label>
              <input
                type="file"
                id="image"
                ref={imageRef}
                onChange={(e) => {
                  handleImageChange(e);
                  setImageType("main-image");
                }}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              />
            </div>

            {showCropper && tempImageUrl && (
              <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                  <Cropper
                    image={tempImageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={9 / 16}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                  <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCropper(false);
                        // remove the image
                        if (imageType === "main-image") {
                          if (imageRef.current) {
                            imageRef.current.value = "";
                          }
                        } else if (imageType === "mobile-header-image") {
                          if (mobileHeaderImageRef.current) {
                            mobileHeaderImageRef.current.value = "";
                          }
                        } else if (imageType === "card-image") {
                          if (cardImageRef.current) {
                            cardImageRef.current.value = "";
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCropConfirm(imageType)}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Crop & Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="fldWrap">
              <label htmlFor="mobileHeaderImage">
                <span>Mobile Header Image (Optional)</span>
              </label>
              <input
                type="file"
                id="mobileHeaderImage"
                ref={mobileHeaderImageRef}
                onChange={(e) => {
                  handleImageChange(e);
                  setImageType("mobile-header-image");
                }}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              />
              <p className="text-sm text-gray-500 mt-1">
                This image will be used as the header on mobile devices. If not
                provided, the main image will be used.
              </p>
            </div>

            <div className="fldWrap">
              <label htmlFor="cardImage">
                <span>Card Image (Optional - for explore page)</span>
              </label>
              <input
                type="file"
                id="cardImage"
                ref={cardImageRef}
                onChange={(e) => {
                  handleImageChange(e);
                  setImageType("card-image");
                }}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              />
              <p className="text-sm text-gray-500 mt-1">
                If not provided, the main image will be used for cards
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">
                Upload Video
              </label>
              <VideoUploader
                onDeleteSuccess={() => setVideoUrl("")}
                onUploadSuccess={handleUploadSuccess}
              />
            </div>

            <div className="fldWrap">
              <label htmlFor="imageCredit">
                <span>Image Credit (Optional)</span>
                <CharCounter value={imageCredit} max={50} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  id="imageCredit"
                  maxLength={50}
                  value={imageCredit}
                  onChange={(e) => setImageCredit(e.target.value)}
                  placeholder="e.g., Photo by John Doe"
                  className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                    theme === "dark"
                      ? "bg-gray-700 text-white"
                      : "text-gray-700"
                  } leading-tight focus:outline-none focus:shadow-outline`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="imageCreditLink">
                <span>Image Credit Link (Optional)</span>
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  id="imageCreditLink"
                  maxLength={500}
                  value={imageCreditLink}
                  onChange={(e) => setImageCreditLink(e.target.value)}
                  placeholder="https://www.example.com/@johndoe"
                  className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                    theme === "dark"
                      ? "bg-gray-700 text-white"
                      : "text-gray-700"
                  } leading-tight focus:outline-none focus:shadow-outline`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="imageDescription">
                <span>Image Description</span>
              </label>
              <MarkdownField
                value={imageDescription}
                onChange={(value) => setImageDescription(value || "")}
                placeholder="Describe the image with optional formatting"
                minHeight={150}
              />
            </div>

            <div className="fldWrap">
              <label htmlFor="flRating">
                <span>FL Rating</span>
              </label>
              <select
                id="flRating"
                value={flRating}
                onChange={(e) => setFlRating(e.target.value)}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              >
                <option value="">Select FL Rating</option>
                <option value="Pee Wee Level 1 | Basic ">
                  Pee Wee <br />
                  Level 1 | Basic
                </option>
                <option value="Two Hand Touch Level 2 | Challenging">
                  Two Hand Touch <br />
                  Level 2 | Challenging
                </option>
                <option value="Full Contact Level 3 | Serious">
                  Full Contact <br />
                  Level 3 | Serious
                </option>
                <option value="All Pro Elite Level 4 | Ruthless">
                  All Pro Elite <br />
                  Level 4 | Ruthless
                </option>
                <option value="No Mercy Level 5 | Insane">
                  No Mercy <br /> Level 5 | Insane
                </option>
              </select>
            </div>

            <div className="fldWrap">
              <label htmlFor="embarrassmentLevel">
                <span>{contentType === "draftIdeas" ? "Effort" : "Shame"}</span>
              </label>
              <select
                id="embarrassmentLevel"
                value={embarrassmentLevel}
                onChange={(e) => setEmbarrassmentLevel(e.target.value)}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              >
                <option value="">Select level</option>
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num.toString()}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="fldWrap">
              <label htmlFor="riskLevel">
                <span>{contentType === "draftIdeas" ? "Skill" : "Danger"}</span>
              </label>
              <select
                id="riskLevel"
                value={riskLevel}
                onChange={(e) => setRiskLevel(e.target.value)}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              >
                <option value="">Select level</option>
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num.toString()}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="fldWrap">
              <label htmlFor="longTermEffects">
                <span>
                  {contentType === "draftIdeas" ? "Physicality" : "Effort"}
                </span>
              </label>
              <select
                id="longTermEffects"
                value={longTermEffects}
                onChange={(e) => setLongTermEffects(e.target.value)}
                className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                  theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              >
                <option value="">Select level</option>
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num.toString()}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="fldWrap">
              <label htmlFor="punishmentLocation">
                <span>Punishment Location (main heading)</span>
                <CharCounter value={equipmentLocker} max={30} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={equipmentLocker}
                  onChange={(e) => setEquipmentLocker(e.target.value)}
                  maxLength={30}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                  placeholder="Enter punishment location details"
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="punishmentLocationSub">
                <span>Punishment Location (sub heading)</span>
                <CharCounter value={equipmentLockerSubHeading} max={80} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={equipmentLockerSubHeading}
                  onChange={(e) => setEquipmentLockerSubHeading(e.target.value)}
                  maxLength={80}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                  placeholder="Enter punishment location details"
                />
              </div>
            </div>

            <div className="fldWrap" style={{ display: "none" }}>
              <label htmlFor="equipmentNeeded">
                <span>Equipment Needed</span>
              </label>
              {equipmentNeeded.map((item, index) => (
                <div key={index} className="flex mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleEquipmentChange(index, e.target.value)
                    }
                    className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                      theme === "dark"
                        ? "bg-gray-700 text-white"
                        : "text-gray-700"
                    } leading-tight focus:outline-none focus:shadow-outline`}
                  />
                  {index === equipmentNeeded.length - 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEquipmentNeeded([...equipmentNeeded, ""])
                      }
                      className="ml-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Equipment Products Section */}
            <div className="mb-8 border-t border-gray-300 pt-6">
              <label className="block text-lg font-bold mb-4">
                Equipment Products (Optional)
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Add products with links and images that correspond to the
                equipment needed for this punishment.
              </p>

              {equipmentProducts.map((product, index) => (
                <div
                  key={index}
                  className="mb-6 p-4 border border-gray-300 rounded-lg"
                >
                  <div className="mb-3">
                    <label className="block text-sm font-bold mb-1">
                      Product Name
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) =>
                          handleEquipmentProductChange(
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        maxLength={100}
                        className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                          theme === "dark"
                            ? "bg-gray-700 text-white"
                            : "text-gray-700"
                        } leading-tight focus:outline-none focus:shadow-outline`}
                        placeholder="e.g., Basketball"
                      />
                      <CharCounter value={product.name} max={100} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-bold mb-1">
                      Product Link
                    </label>
                    <input
                      type="url"
                      value={product.link || ""}
                      onChange={(e) =>
                        handleEquipmentProductChange(
                          index,
                          "link",
                          e.target.value
                        )
                      }
                      className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                        theme === "dark"
                          ? "bg-gray-700 text-white"
                          : "text-gray-700"
                      } leading-tight focus:outline-none focus:shadow-outline`}
                      placeholder="https://example.com/product"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-bold mb-1">
                      Product Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleProductImageChange(index, e.target.files[0]);
                        }
                      }}
                      className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                        theme === "dark"
                          ? "bg-gray-700 text-white"
                          : "text-gray-700"
                      } leading-tight focus:outline-none focus:shadow-outline`}
                    />
                    {product.image && typeof product.image === "string" && (
                      <div className="mt-2 w-40 h-40 relative">
                        <img
                          src={product.image}
                          alt={product.name || "Product preview"}
                          className="w-full h-full object-contain border rounded"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const updatedProducts = [...equipmentProducts];
                        updatedProducts.splice(index, 1);
                        setEquipmentProducts(updatedProducts);
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Remove Product
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  setEquipmentProducts([...equipmentProducts, { name: "" }])
                }
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                Add Equipment Product
              </button>
            </div>

            <div
              className="mb-4 border-t border-gray-300 pt-6"
              style={{ display: "none" }}
            >
              <label className="block text-sm font-bold mb-2">
                Legacy Equipment Link (Optional)
              </label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-1/2">
                  <label className="block text-xs mb-1">Equipment Image</label>
                  <input
                    type="file"
                    id="equipmentImage"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setEquipmentImage(e.target.files[0]);
                      }
                    }}
                    className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                      theme === "dark"
                        ? "bg-gray-700 text-white"
                        : "text-gray-700"
                    } leading-tight focus:outline-none focus:shadow-outline`}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <label className="block text-xs mb-1">
                    Equipment Image Link
                  </label>
                  <input
                    type="url"
                    id="equipmentImageLink"
                    value={equipmentImageLink}
                    onChange={(e) => setEquipmentImageLink(e.target.value)}
                    placeholder="https://example.com/product"
                    className={`shadow appearance-none border rounded w-full py-2 px-3 ${
                      theme === "dark"
                        ? "bg-gray-700 text-white"
                        : "text-gray-700"
                    } leading-tight focus:outline-none focus:shadow-outline`}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                This single equipment link is kept for backward compatibility.
                We recommend using the multiple equipment products above
                instead.
              </p>
            </div>

            <div className="fldWrap">
              <label htmlFor="timeComplete">
                <span>Time to Complete (main heading)</span>
                <CharCounter value={timeToComplete} max={30} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={timeToComplete}
                  maxLength={30}
                  onChange={(e) => setTimeToComplete(e.target.value)}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="timeCompleteSub">
                <span>Time to Complete (sub heading)</span>
                <CharCounter value={timeToCompleteSubHeading} max={80} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  maxLength={80}
                  value={timeToCompleteSubHeading}
                  onChange={(e) => setTimeToCompleteSubHeading(e.target.value)}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="costComplete">
                <span>Cost to Complete (main heading)</span>
                <CharCounter value={costToComplete} max={30} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={costToComplete}
                  onChange={(e) => setCostToComplete(e.target.value)}
                  maxLength={30}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="costCompleteSub">
                <span>Cost to Complete (sub heading)</span>
                <CharCounter value={costToCompleteSubHeading} max={80} />
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={costToCompleteSubHeading}
                  onChange={(e) => setCostToCompleteSubHeading(e.target.value)}
                  maxLength={80}
                  className={`w-full p-2 rounded ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </div>
            </div>

            <div className="fldWrap">
              <label htmlFor="waysSpice">
                <span>Ways to Spice It Up</span>
              </label>
              {waysToSpiceUp.map((way, index) => (
                <div key={index} className="mb-4">
                  <MarkdownField
                    value={way}
                    onChange={(value) =>
                      handleWaysToSpiceUpChange(index, value || "")
                    }
                    placeholder="Enter way to spice it up with optional formatting"
                    minHeight={100}
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  setTitle("");
                  setSubtitle("");
                  setImage(null);
                  setImageDescription("");
                  setFlRating("");
                  setEmbarrassmentLevel("");
                  setRiskLevel("");
                  setLongTermEffects("");
                  setEquipmentLocker("");
                  setEquipmentLockerSubHeading("");
                  setEquipmentNeeded([""]);
                  setTimeToComplete("");
                  setTimeToCompleteSubHeading("");
                  setCostToComplete("");
                  setCostToCompleteSubHeading("");
                  setWaysToSpiceUp([""]);
                  setInstructions([{ description: "" }]);
                  setTags([]);
                  setEquipmentProducts([]);
                  setVideoUrl("");
                  setViewOption("punishments");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="px-6 py-3 bg-[#FF0000] text-white rounded-lg hover:bg-red-700 
                flex items-center gap-2 transition-all duration-200"
              >
                <Trash size={18} />
                Clear
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] 
              flex items-center gap-2 transition-all duration-200 ml-4"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save
                  </>
                )}
              </button>
            </div>

            {error && (
              <div
                className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminForm;
